import {
  type RuleById,
  RuleBadge,
  strongestRuleColor,
  useRuleById,
} from "~/components/rules/ruleColors"
import { ProductTableColumn } from "~/constants/productTableColumns"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { extractVariantSummaries } from "~/utils/productTableUtils"
import { createCustomColumn } from "~/utils/columnTemplates"
import { formatCount } from "~/utils/formatting"
import type { VariantRuleMatch } from "~/utils/variantRules"
import { uniq } from "~/utils/tsUtils"

import type { ColumnSpec } from "./types"

export type RulesColumnCtx = {
  /** Variant id → the rules it matches. */
  matches: ReadonlyMap<string, readonly VariantRuleMatch[]>
  ruleById: RuleById
}

const ruleNameOf = (ruleById: RuleById, id: unknown): string =>
  ruleById.get(Number(id))?.name ?? String(id)

/** What the Rules cell hover reads: one variant's matches, or a group's per-rule counts. */
export type RulesTooltipValue =
  | { kind: "variant"; variantId: string; matches: readonly VariantRuleMatch[] }
  | { kind: "group"; variantCount: number; counts: { ruleId: number; count: number }[] }

type RulesSpec = ColumnSpec<IVariantSummary, RulesColumnCtx>
type RuleMatches = RulesColumnCtx["matches"]

const NO_RULES: readonly number[] = []
const NO_MATCHES: readonly VariantRuleMatch[] = []

export const ruleIdsOf = (value: unknown): readonly number[] =>
  Array.isArray(value) ? (value as number[]) : NO_RULES

export const formatRuleIds = (value: unknown, ruleById: RuleById): string =>
  ruleIdsOf(value)
    .map((id) => ruleNameOf(ruleById, id))
    .join(", ")

const matchedIds = (matches: RuleMatches, variantId: string): readonly number[] =>
  matches.get(variantId)?.map((m) => m.ruleId) ?? NO_RULES

/** The rules any of `variantIds` matches, for a row covering one or several variants. */
export const ruleIdsFor = (variantIds: readonly string[], matches: RuleMatches): number[] =>
  uniq(variantIds.flatMap((id) => matchedIds(matches, id)))

export function rulesTooltipValue(
  variantIds: readonly string[],
  matches: RuleMatches,
): RulesTooltipValue | undefined {
  if (variantIds.length === 1) {
    const variantMatches = matches.get(variantIds[0]!) ?? NO_MATCHES
    return variantMatches.length > 0
      ? { kind: "variant", variantId: variantIds[0]!, matches: variantMatches }
      : undefined
  }
  const counts = new Map<number, number>()
  for (const id of variantIds) {
    for (const ruleId of matchedIds(matches, id)) counts.set(ruleId, (counts.get(ruleId) ?? 0) + 1)
  }
  if (counts.size === 0) return undefined
  return {
    kind: "group",
    variantCount: variantIds.length,
    counts: [...counts].map(([ruleId, count]) => ({ ruleId, count })),
  }
}

export const RULES_COLUMN: RulesSpec = {
  colId: ProductTableColumn.Rules,
  calculate: (row, ctx) => matchedIds(ctx.matches, row.variantId),
  aggregate: (leaves, ctx) =>
    ruleIdsFor(
      leaves.map((leaf) => leaf.variantId),
      ctx.matches,
    ),
  compare: (a, b) => ruleIdsOf(a).length - ruleIdsOf(b).length,
  render: (params) => {
    const ids = ruleIdsOf(params.value)
    return ids.length === 0 ? null : <RulesCell ruleIds={ids} />
  },
  colDef: (ctx) =>
    createCustomColumn<IVariantSummary>({
      colId: ProductTableColumn.Rules,
      sortingOrder: ["desc", "asc", null],
      centerContent: true,
      valueFormatter: (params) => formatRuleIds(params.value, ctx.ruleById),
      tooltipComponentParams: { tooltipType: "Rules" },
      tooltipValueGetter: (params) =>
        rulesTooltipValue(
          extractVariantSummaries(params).map((v) => v.variantId),
          ctx.matches,
        ),
      minWidth: 200,
    }),
}

/** One match shows its name in the rule's colour; several collapse to a count in the most severe colour (the hover lists them). */
export function RulesCell({ ruleIds }: { ruleIds: readonly number[] }) {
  const ruleById = useRuleById()
  const rules = ruleIds.flatMap((id) => ruleById.get(id) ?? [])
  if (rules.length === 0) return null
  return rules.length === 1 ? (
    <RuleBadge color={rules[0]!.color}>{rules[0]!.name}</RuleBadge>
  ) : (
    <RuleBadge color={strongestRuleColor(rules.map((r) => r.color))}>
      {formatCount(rules.length, "rule")}
    </RuleBadge>
  )
}

export const RULES_COLUMN_SPECS: readonly RulesSpec[] = [RULES_COLUMN]
